import { useState as _useState, useEffect as _useEffect } from 'react';
import { MASTER_VOLUME } from './audioLevel';
import { markActive, trackPlayback } from './practiceTime';

// Learned chars-per-second cache, keyed by `${voiceURI}@${rate}`. We measure the
// real speech duration on every utterance and learn how fast each voice actually
// speaks at each rate, so the estimated highlight self-calibrates (important for
// voices that don't fire native boundary events, and at slow rates which aren't
// linear). Persisted so it's accurate from the first phrase on repeat visits.
const CPS_KEY = 'mimique_voice_cps';
const _readCps = () => {
    try { return JSON.parse(localStorage.getItem(CPS_KEY) || '{}'); } catch { return {}; }
};
const _recordCps = (key, cps) => {
    if (!key || !(cps > 0) || !isFinite(cps)) return;
    try {
        const all = _readCps();
        // Exponential moving average so a one-off glitch doesn't skew it.
        all[key] = all[key] ? all[key] * 0.6 + cps * 0.4 : cps;
        localStorage.setItem(CPS_KEY, JSON.stringify(all));
    } catch { /* localStorage unavailable */ }
};

// Text-to-Speech utility using Web Speech API
// Returns a promise that resolves with the playback duration in ms
export const speak = (text, lang = 'fr-FR', rate = 1.0, voiceURI = null, onBoundary = null) => {
    return new Promise((resolve, reject) => {
        if (!('speechSynthesis' in window)) {
            reject(new Error('Text-to-speech not supported'));
            return;
        }

        // Fire-and-forget voice warm-up (Android loads voices lazily).
        try { window.speechSynthesis.getVoices(); } catch { /* noop */ }

        let estimatedTimers = [];
        const clearEstimated = () => {
            estimatedTimers.forEach(t => clearTimeout(t));
            estimatedTimers = [];
        };

        // --- Karaoke highlighting (estimated, identical on every browser) ---
        // Native `onboundary` events lag the audio badly on many voices/engines, so we
        // drive the highlight from an estimated schedule: each word is scheduled by its
        // character-position proportion across the estimated speech duration, anchored
        // to onstart so it can't lead the voice.
        const scheduleEstimated = (cpsKey) => {
            if (!onBoundary) return;
            const wordRegex = /\S+/g;
            let m;
            const wordPositions = [];
            while ((m = wordRegex.exec(text)) !== null) {
                // Skip pure-punctuation tokens (French puts a space before ? ! : ;)
                if (!/[\p{L}\p{N}]/u.test(m[0])) continue;
                wordPositions.push(m.index);
            }
            // Use the learned chars/sec for this voice+rate if we've measured it
            // before; otherwise start from a baseline (~15 cps at 1x, nudged down at
            // slow rates which aren't linear). effectiveCps already folds in the rate.
            const baselineCps = 15 * rate * (rate < 1 ? 0.92 : 1);
            const effectiveCps = _readCps()[cpsKey] || baselineCps;
            const estimatedTotalMs = (text.length / effectiveCps) * 1000;
            // Compress the whole schedule slightly (rather than clamping the tail to a
            // single time, which made the last words all highlight at once) so every
            // word keeps its relative spacing and the final word still lands with a
            // little margin before the phrase ends.
            const SCALE = 0.93;
            wordPositions.forEach((charIndex) => {
                const wordDelay = (charIndex / text.length) * estimatedTotalMs * SCALE;
                const t = setTimeout(() => onBoundary(charIndex), wordDelay);
                estimatedTimers.push(t);
            });
        };

        // Chrome (esp. on Android) frequently throws "synthesis-failed" on the very
        // first utterance after page load or after a cancel(), but succeeds on a
        // quick retry. So attempt up to 3 times before surfacing the error.
        let attempt = 0;
        const MAX_ATTEMPTS = 3;

        const trySpeak = () => {
            // Was there actually something to cancel? Chrome fails an utterance
            // with "synthesis-failed" when speak() lands too soon after cancel()
            // on a busy engine, so that case gets a breath before speaking.
            const wasBusy = window.speechSynthesis.speaking || window.speechSynthesis.pending;
            // Cancel any ongoing speech
            window.speechSynthesis.cancel();
            // Wake the engine if it silently suspended itself during an idle gap
            // (Samsung Internet / some Android browsers do this after ~3s of inactivity)
            if (window.speechSynthesis.paused) window.speechSynthesis.resume();

            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = lang;
            utterance.rate = rate;
            utterance.pitch = 1;
            utterance.volume = MASTER_VOLUME;

            // Use the selected voice, or fall back to a voice that matches the
            // requested language (prefer an exact lang match, then the language
            // family). Falling back by language is important so e.g. en-GB text
            // isn't read by a French voice.
            const voices = window.speechSynthesis.getVoices();
            let chosenVoice = null;
            if (voiceURI) {
                const selected = voices.find(v => v.voiceURI === voiceURI);
                if (selected) { utterance.voice = selected; chosenVoice = selected; }
            } else {
                const langLower = (lang || '').toLowerCase();
                const family = langLower.split('-')[0];
                const match =
                    voices.find(v => v.lang.toLowerCase() === langLower) ||
                    voices.find(v => v.lang.toLowerCase().startsWith(family));
                if (match) { utterance.voice = match; chosenVoice = match; }
            }

            // Prefer the engine's real word-boundary events when the chosen voice is
            // known to fire them reliably (detected + cached by useVoiceCapabilities).
            // They track the audio exactly at any playback rate, which the estimated
            // schedule can't — especially at slow speeds. Fall back to the estimate
            // for voices that don't fire usable boundary events.
            const caps = getVoiceCapabilities();
            const useNative = !!onBoundary && chosenVoice && caps[chosenVoice.voiceURI] === true;

            const startTime = Date.now();
            let speechStartedAt = startTime;
            let started = false;      // did onstart fire (i.e. audio actually began)?
            let stale = false;        // superseded by a watchdog retry — ignore late events
            let startWatchdog = null;
            let endWatchdog = null;   // iOS: onend often never fires — settle anyway
            const cpsKey = `${chosenVoice?.voiceURI || 'default'}@${rate}`;

            if (useNative) {
                utterance.onboundary = (event) => {
                    if (event.name === 'word' || event.name === undefined) {
                        onBoundary(event.charIndex);
                    }
                };
            }

            utterance.onstart = () => {
                started = true;
                clearTimeout(startWatchdog);
                speechStartedAt = Date.now();
                if (!useNative) scheduleEstimated(cpsKey);
                // iOS WebKit (incl. Chrome iOS) frequently never fires onend for the
                // 2nd+ utterance in a session, which would hang the awaiting caller
                // (the Automatic loop) forever — the phrase plays, then no progression.
                // Guarantee the promise settles: after the estimated speech duration
                // plus a generous margin, treat it as ended if onend hasn't fired.
                const baselineCps = 15 * rate * (rate < 1 ? 0.92 : 1);
                const effectiveCps = _readCps()[cpsKey] || baselineCps;
                const estMs = (text.length / effectiveCps) * 1000;
                endWatchdog = setTimeout(() => {
                    if (stale) return;
                    clearEstimated();
                    if (onBoundary) onBoundary(-1); // clear highlight
                    resolve(Date.now() - startTime);
                }, estMs * 1.5 + 2500);
            };
            utterance.onend = () => {
                if (stale) return; // a retry took over; ignore this dropped utterance
                clearTimeout(startWatchdog);
                clearTimeout(endWatchdog);
                clearEstimated();
                if (onBoundary) onBoundary(-1); // clear highlight
                // Learn how fast this voice actually spoke (chars/sec) so the
                // estimated schedule self-calibrates next time.
                const spokenMs = Date.now() - speechStartedAt;
                if (spokenMs > 250 && text.length > 3) {
                    _recordCps(cpsKey, text.length / (spokenMs / 1000));
                }
                resolve(Date.now() - startTime);
            };
            utterance.onerror = (e) => {
                if (stale) return; // a retry took over; ignore this dropped utterance
                clearTimeout(startWatchdog);
                clearTimeout(endWatchdog);
                clearEstimated();
                const code = (e && e.error) || 'unknown';
                // "interrupted" / "canceled" happen when our own cancel() pre-empts a
                // queued utterance — not a real failure, so resolve quietly.
                if (code === 'interrupted' || code === 'canceled') {
                    resolve(0);
                    return;
                }
                // Retry transient engine failures with a fresh utterance.
                if ((code === 'synthesis-failed' || code === 'synthesis-unavailable') && attempt < MAX_ATTEMPTS - 1) {
                    attempt++;
                    // Backing off further each time. A flat 250ms was not enough
                    // to clear whatever state the engine was in: three failures
                    // in a row still ended as a red toast.
                    setTimeout(trySpeak, 250 * attempt);
                    return;
                }
                const err = new Error('tts-error');
                err.error = code;
                reject(err);
            };

            // Free accounts are the ones that meet this: they speak through the
            // browser rather than ElevenLabs, and the faster the practice loop
            // runs the closer speak() lands to the previous cancel().
            if (wasBusy) setTimeout(() => { if (!stale) window.speechSynthesis.speak(utterance); }, 120);
            else window.speechSynthesis.speak(utterance);

            // Silent-drop watchdog: on a fresh page load Chrome sometimes accepts an
            // utterance but never speaks it — onstart/onend/onerror all stay silent,
            // so the audio just goes missing (this is why the first English line of an
            // Automatic session was skipped unless the engine had already been used).
            // If onstart hasn't fired shortly after speak(), treat it as a drop and
            // retry once with a fresh utterance.
            startWatchdog = setTimeout(() => {
                if (started || stale) return;
                if (attempt < MAX_ATTEMPTS - 1) {
                    attempt++;
                    stale = true; // ignore the dropped utterance's late events
                    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
                    trySpeak();
                } else {
                    // Final attempt hasn't started within 1s. Don't force-fail — a slow
                    // engine may still start late (some Android devices have >1s start
                    // latency), and if it does, onstart/onend resolve normally. But add a
                    // long, non-destructive backstop so the caller can't hang forever if
                    // the utterance is truly dead. It doesn't cancel or mark stale, so a
                    // late-but-working utterance is untouched; a late resolve here is a
                    // harmless no-op once the promise has already settled.
                    setTimeout(() => {
                        if (stale || started) return;
                        clearEstimated();
                        if (onBoundary) onBoundary(-1);
                        resolve(0);
                    }, 8000);
                }
            }, 1000);
        };

        trySpeak();
    });
};

// iOS Safari/WebKit blocks gesture-less speechSynthesis.speak() (and getUserMedia):
// the first call must originate from a user gesture or it is silently dropped. This
// is why Automatic mode gates its start behind a tap on iOS (see Automatic.js) —
// auto-starting from an effect leaves the TTS silent.
export const IS_IOS = typeof navigator !== 'undefined' && (
    /iP(ad|hone|od)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1)
);

// Chrome (desktop especially) silently drops the VERY FIRST speechSynthesis
// utterance after a page load — nothing plays and no error fires. In Automatic
// mode the first utterance is the English translation, so it goes missing on a
// fresh session unless the engine was already used (e.g. by playing a Manual
// phrase first). Call this from the Start user gesture to prime the engine with
// a silent throwaway utterance, so the first *real* utterance isn't the one
// that gets swallowed.
export const warmUpSpeech = () => {
    if (!('speechSynthesis' in window)) return;
    try {
        window.speechSynthesis.cancel();
        // Real text, not whitespace: Chrome ignores whitespace-only utterances,
        // so they don't "use up" the first-utterance drop. Volume 0 = inaudible.
        const u = new SpeechSynthesisUtterance('a');
        u.volume = 0;
        window.speechSynthesis.speak(u);
    } catch { /* best-effort */ }
};

// Get all available French voices
export const getFrenchVoices = () => {
    const voices = window.speechSynthesis.getVoices();
    return voices.filter(v => v.lang.startsWith('fr'));
};

// Playback speed helpers
const SPEED_KEY = 'mimique_playbackSpeed';
export const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25];
export const SPEED_LABELS = { 0.5: 'Slow (x0.5)', 0.75: 'Slower (x0.75)', 1: 'Normal (x1)', 1.25: 'Faster (x1.25)' };

export const getSavedSpeed = () => {
    const saved = localStorage.getItem(SPEED_KEY);
    const parsed = saved ? parseFloat(saved) : 1;
    return SPEED_OPTIONS.includes(parsed) ? parsed : 1;
};

export const saveSpeed = (speed) => {
    localStorage.setItem(SPEED_KEY, String(speed));
};

// Playbacks per phrase helpers (setting removed from UI; kept for Automatic runtime)
const PLAYBACKS_KEY = 'mimique_playbacksPerPhrase';
export const PLAYBACKS_OPTIONS = [1, 2, 3, 4, 5];
export const getSavedPlaybacks = () => {
    const saved = localStorage.getItem(PLAYBACKS_KEY);
    const parsed = saved ? parseInt(saved, 10) : 1;
    return PLAYBACKS_OPTIONS.includes(parsed) ? parsed : 1;
};
export const savePlaybacks = (n) => {
    localStorage.setItem(PLAYBACKS_KEY, String(n));
};

// Karaoke (word highlighting) helpers
const KARAOKE_KEY = 'mimique_karaokeEnabled';
export const getSavedKaraoke = () => localStorage.getItem(KARAOKE_KEY) !== 'false';
export const saveKaraoke = (v) => localStorage.setItem(KARAOKE_KEY, String(v));

// Play English translation helper — when on, the automatic loop also speaks the
// English translation of each phrase. On by default.
const PLAY_ENGLISH_KEY = 'mimique_playEnglish';
export const getSavedPlayEnglish = () => localStorage.getItem(PLAY_ENGLISH_KEY) !== 'false';
export const savePlayEnglish = (v) => localStorage.setItem(PLAY_ENGLISH_KEY, String(v));

// Practice flow helpers — 'manual' (one phrase at a time, user-driven) or
// 'automatic' (hands-free loop). Manual is the default. Manual maps to the
// /manual page, automatic to the /automatic page.
const FLOW_KEY = 'mimique_practiceFlow';
export const getSavedFlow = () => (localStorage.getItem(FLOW_KEY) === 'automatic' ? 'automatic' : 'manual');
export const saveFlow = (v) => localStorage.setItem(FLOW_KEY, v === 'automatic' ? 'automatic' : 'manual');
// Route base for a given flow, with the shared params appended by the caller.
export const flowPath = (flow) => (flow === 'automatic' ? '/automatic' : '/manual');

// Practice order helpers — 'smart' (low scores first), 'random' (shuffled) or
// 'added' (the order phrases were added). Smart is the default. Configured in
// Settings, applied to every session.
const ORDER_KEY = 'mimique_practiceOrder';
const ORDER_VALUES = ['smart', 'random', 'added'];
export const getSavedOrder = () => {
    const v = localStorage.getItem(ORDER_KEY);
    return ORDER_VALUES.includes(v) ? v : 'smart';
};
export const saveOrder = (v) => localStorage.setItem(ORDER_KEY, ORDER_VALUES.includes(v) ? v : 'smart');

// Session length target helpers
const SESSION_LENGTH_KEY = 'mimique_sessionLengthTarget';
export const SESSION_LENGTH_OPTIONS = [5, 10, 15, 20, 30];
export const getSavedSessionLength = () => {
    const saved = localStorage.getItem(SESSION_LENGTH_KEY);
    const parsed = saved ? parseInt(saved, 10) : 10;
    return SESSION_LENGTH_OPTIONS.includes(parsed) ? parsed : 10;
};
export const saveSessionLength = (n) => {
    localStorage.setItem(SESSION_LENGTH_KEY, String(n));
};

// ── Voice capability detection (does the voice fire onboundary?) ─────────
const CAPS_KEY = 'mimique_voice_capabilities';
let _capsCache = null;
let _capsPromise = null;

const readCachedCaps = () => {
    try {
        const raw = localStorage.getItem(CAPS_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const writeCachedCaps = (caps) => {
    try {
        localStorage.setItem(CAPS_KEY, JSON.stringify(caps));
    } catch { /* localStorage unavailable (private mode) */ }
};

// Probe a single voice with a short, silent utterance. Resolves true if at
// least one onboundary event fires before the utterance ends or we time out.
const testVoiceBoundary = (voice) => {
    return new Promise((resolve) => {
        if (!('speechSynthesis' in window)) { resolve(false); return; }
        let fired = false;
        let settled = false;
        let timeoutId;
        const finish = (val) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            try { window.speechSynthesis.cancel(); } catch { /* noop */ }
            resolve(val);
        };
        try {
            const utter = new SpeechSynthesisUtterance('test test');
            utter.voice = voice;
            utter.lang = voice.lang || 'fr-FR';
            utter.volume = 0;
            utter.rate = 2; // speed up so detection is fast but reliable
            utter.onboundary = (event) => {
                if (event.name === 'word' || event.name === undefined) {
                    fired = true;
                }
            };
            utter.onend = () => finish(fired);
            utter.onerror = () => finish(false);
            timeoutId = setTimeout(() => finish(fired), 2500);
            window.speechSynthesis.cancel();
            window.speechSynthesis.speak(utter);
        } catch {
            finish(false);
        }
    });
};

// Run detection sequentially for any French voice not already cached.
// Idempotent: a single in-flight promise is shared across callers.
export const detectVoiceCapabilities = () => {
    if (_capsPromise) return _capsPromise;
    _capsPromise = (async () => {
        await loadVoices();
        const cached = readCachedCaps();
        const french = getFrenchVoices();
        let updated = false;
        for (const v of french) {
            if (cached[v.voiceURI] === undefined) {
                let supports;
                try {
                    supports = await testVoiceBoundary(v);
                } catch {
                    supports = false; // err on the side of "no karaoke"
                }
                cached[v.voiceURI] = supports;
                updated = true;
            }
        }
        if (updated) writeCachedCaps(cached);
        _capsCache = cached;
        return cached;
    })();
    return _capsPromise;
};

// Synchronous read for code that just wants whatever is already cached.
export const getVoiceCapabilities = () => _capsCache || readCachedCaps();

// React hook: returns { caps, ready }. Triggers detection on first mount and
// re-renders consumers once it resolves.
export const useVoiceCapabilities = () => {
    const [state, setState] = _useState(() => ({
        caps: getVoiceCapabilities(),
        ready: !!_capsCache,
    }));
    _useEffect(() => {
        let cancelled = false;
        detectVoiceCapabilities().then((caps) => {
            if (!cancelled) setState({ caps, ready: true });
        });
        return () => { cancelled = true; };
    }, []);
    return state;
};

// Voice preference helpers
const VOICE_KEY = 'mimique_selectedVoice';

export const getSavedVoice = () => {
    return localStorage.getItem(VOICE_KEY) || '';
};

export const saveVoice = (voiceURI) => {
    localStorage.setItem(VOICE_KEY, voiceURI);
};

// Query the mic permission state ('granted' | 'denied' | 'prompt') via the
// Permissions API, or null where unsupported. 'prompt' immediately before a
// getUserMedia call means the browser is about to show its permission popup —
// used to track how often users face the popup (especially repeat popups on
// iOS, which re-prompts even after access was granted).
export const getMicPermissionState = async () => {
    try {
        const status = await navigator.permissions.query({ name: 'microphone' });
        return status.state;
    } catch {
        return null;
    }
};

// Mic constraints: keep echo cancellation ON so TTS played through the phone
// speaker doesn't feed back into the mic during recording (Android reported
// loud static/feedback without headphones). Keep autoGainControl OFF: with AGC
// on, Android amplifies ambient noise during silence, so a "said nothing"
// recording arrives as loud noise that defeats silence detection.
const MIC_CONSTRAINTS = {
    echoCancellation: true,
    noiseSuppression: false,
    autoGainControl: false,
};
// Echo cancellation off. On Android Chrome it is echo cancellation that puts an
// open mic into the "voice call" audio mode, which silenced the hands-free beep;
// that is why the mic was opened only after the beep, and the first word was
// lost while it opened (18 Oct 2026). Without it the mic can be opened BEFORE
// the beep. Nothing plays while recording in hands-free mode, so there is no
// echo to cancel. Being tried on the beta accounts first.
export const MIC_CONSTRAINTS_EARLY = {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
};

// Hard cap on any single recording. Speechace's Basic plan bills per 15-second
// call and hard-caps at ~30s; capping the recorder at 29s keeps every clip
// scoreable by Speechace so scoring stays consistent across clip lengths.
export const MAX_RECORDING_MS = 29000;

// What both practice modes say when a recording is cut off at the cap. Kept
// here so Manual and Automatic cannot end up explaining it differently.
export const RECORDING_CAPPED_MESSAGE =
    "You've hit the 30-second limit, so your recording stopped there. "
    + "Shorter phrases score much more accurately.";

// Playing the learner's own recording back, shared by both practice modes.
// iOS rejects play() with NotSupportedError when it cannot read the clip; left
// uncaught, the button stayed on "playing" and nothing told the learner why.
export const PLAYBACK_FAILED_MESSAGE = "Your recording couldn't be played. Record it again to hear it back.";
export const playRecording = (audio, onFail) => {
    trackPlayback(audio);   // listening to your own voice is practice too
    let p;
    try { p = audio.play(); } catch { onFail(); return; }
    if (p && p.catch) p.catch(onFail);
};

// Roughly how fast a learner reads French aloud. Deliberately pessimistic: it
// is the pace of someone sounding a phrase out, not a native reading it, and
// the point is to warn before a recording gets cut off rather than after.
//
// Grounded in real clips: a 51-word phrase fitted inside the 29s window, a
// 38-word one did not, because the second had pauses in it. Pauses are normal
// for a learner, so the estimate assumes them.
const LEARNER_WORDS_PER_SEC = 1.6;

// How long this phrase is likely to take to say, in seconds.
export const estimateSpokenSeconds = (text) => {
    const words = String(text || '').trim().split(/\s+/).filter(Boolean).length;
    return words ? words / LEARNER_WORDS_PER_SEC : 0;
};

// True when a phrase is long enough that the recording may be cut off before
// they finish, which costs them the acoustic score for that attempt. The
// margin is deliberate: an estimate that only warns at the exact limit warns
// too late for anyone who pauses.
export const isPhraseTooLong = (text) =>
    estimateSpokenSeconds(text) > (MAX_RECORDING_MS / 1000) * 0.8;

// What the recorder asks for, in order. iPhones go mp4 first: since Safari 18.4
// they accept webm too, but their webm recordings often arrive as a 5 byte file
// nothing can read (29 September 2026, caught on a real iPhone), while mp4 is
// what they recorded reliably before webm existed. Shared by both practice modes.
const RECORDER_TYPES = IS_IOS
    ? ['audio/mp4', 'audio/webm; codecs=opus']
    : ['audio/ogg; codecs=opus', 'audio/webm; codecs=opus', 'audio/mp4'];

// Audio Recorder class for recording user's voice
// ── Recording diagnostics, test accounts only (19 Oct 2026) ─────────────────
// A timeline of what the mic and the recorder did during one recording, sent
// with the recording and saved next to it, so a recording problem (silent gaps
// on desktop in Automatic) can be traced to its cause instead of guessed at.
// Off for everyone else: setRecDiag is only switched on by the server for the
// accounts in SPEECHACE_CAPTURE_EMAILS.
let recDiagOn = false;
export const setRecDiag = (on) => { recDiagOn = !!on; };
export const newRecDiag = (mode) => {
    if (!recDiagOn) return null;
    const t0 = performance.now();
    const d = { mode, ua: navigator.userAgent, events: [] };
    d.mark = (name, data) => {
        try {
            if (d.events.length < 600) d.events.push({ t: Math.round(performance.now() - t0), name, ...(data || {}) });
        } catch { /* never let diagnostics break a recording */ }
    };
    d.toJSON = () => ({ mode: d.mode, ua: d.ua, events: d.events });
    return d;
};

export class AudioRecorder {
    constructor() {
        this.mediaRecorder = null;
        this.audioChunks = [];
        this.audioBlob = null;
        this.audioUrl = null;
        this.stream = null;
        // Fires when the hard cap forces the recorder to stop before the caller
        // asked it to. Pages set this to trigger their normal stop-and-analyze
        // flow. No-op by default.
        this.onAutoStopped = null;
        this._maxTimer = null;
        // Raw details of the last getUserMedia failure, for diagnostic analytics.
        this.lastErrorName = '';
        this.lastErrorMessage = '';
        // Recording diagnostics (newRecDiag), set by the page when they are on.
        this.diag = null;
        this._openMs = null;
        // A mic stream lent by the page for a whole session (Automatic on
        // desktop): recorded from, never closed here.
        this.keepStream = false;
    }

    // Record from a mic stream the page keeps open for the whole session. Some
    // laptop mics send ~0.7 s of total silence about 1.4 s after being switched
    // on (Leanne's Realtek mic, every Automatic recording, 19 Oct 2026); opened
    // once for the session, that happens before the first recording, as in
    // Manual, which opens its mic when the page loads.
    useStream(stream) {
        this.stream = stream;
        this.keepStream = true;
        this._openMs = 0;
        return true;
    }

    // Watches the mic track and the recorder for the diagnostics timeline.
    _diagWatch(how) {
        const d = this.diag;
        if (!d) return;
        try {
            const track = this.stream?.getAudioTracks?.()[0];
            if (track) {
                const st = track.getSettings ? track.getSettings() : {};
                d.mark('recorder_start', {
                    how, session_mic: this.keepStream, open_ms: this._openMs, label: track.label, mime: this.mimeType,
                    sample_rate: st.sampleRate, channels: st.channelCount, echo: st.echoCancellation,
                    noise: st.noiseSuppression, agc: st.autoGainControl, muted: track.muted, state: track.readyState,
                });
                track.addEventListener('mute', () => d.mark('track_mute'));
                track.addEventListener('unmute', () => d.mark('track_unmute'));
                track.addEventListener('ended', () => d.mark('track_ended'));
            }
            const mr = this.mediaRecorder;
            if (mr) {
                mr.addEventListener('start', () => d.mark('mr_start'));
                mr.addEventListener('dataavailable', (e) => d.mark('mr_chunk', { size: e.data?.size }));
                mr.addEventListener('error', (e) => d.mark('mr_error', { err: String(e?.error?.name || e) }));
                mr.addEventListener('pause', () => d.mark('mr_pause'));
                mr.addEventListener('resume', () => d.mark('mr_resume'));
                mr.addEventListener('stop', () => d.mark('mr_stop'));
            }
            if (navigator.mediaDevices?.addEventListener) {
                this._diagDevice = () => d.mark('devicechange');
                navigator.mediaDevices.addEventListener('devicechange', this._diagDevice);
            }
        } catch { /* diagnostics only */ }
    }

    _diagUnwatch() {
        try {
            if (this._diagDevice) navigator.mediaDevices.removeEventListener('devicechange', this._diagDevice);
        } catch { /* diagnostics only */ }
        this._diagDevice = null;
    }

    _armMaxTimer() {
        if (this._maxTimer) clearTimeout(this._maxTimer);
        this._maxTimer = setTimeout(() => {
            this._maxTimer = null;
            if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
                try { this.mediaRecorder.stop(); } catch { /* ignore */ }
                this._endSpeaking();
                if (typeof this.onAutoStopped === 'function') {
                    try { this.onAutoStopped(); } catch { /* ignore */ }
                }
            }
        }, MAX_RECORDING_MS);
    }

    _clearMaxTimer() {
        if (this._maxTimer) { clearTimeout(this._maxTimer); this._maxTimer = null; }
    }

    // A second copy of the recording as raw samples, taken straight from the mic
    // and never touching MediaRecorder. iPhones sometimes hand back a recording
    // file that nothing can read (Speechace, Gemini and ffmpeg all refuse it),
    // and a raw copy is the one thing that cannot be broken that way. It is only
    // sent if the normal file is refused, see analyzeAudio in api.js. iOS only,
    // because that is where the unreadable files come from.
    _startPcmTap() {
        this._pcm = null;
        if (!IS_IOS) return;
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx || !this.stream) return;
            const ctx = new Ctx();
            if (ctx.state === 'suspended') ctx.resume().catch(() => {});
            const source = ctx.createMediaStreamSource(this.stream);
            const node = ctx.createScriptProcessor(4096, 1, 1);
            const chunks = [];
            node.onaudioprocess = (e) => { chunks.push(new Float32Array(e.inputBuffer.getChannelData(0))); };
            source.connect(node);
            node.connect(ctx.destination);
            this._pcm = { ctx, source, node, chunks, rate: ctx.sampleRate };
        } catch { this._pcm = null; }
    }

    // Stops the tap and returns the recording as a WAV blob, or null.
    async _finishPcmTap() {
        const pcm = this._pcm;
        this._pcm = null;
        if (!pcm) return null;
        try {
            pcm.node.onaudioprocess = null;
            pcm.source.disconnect();
            pcm.node.disconnect();
            pcm.ctx.close().catch(() => {});
            const total = pcm.chunks.reduce((n, c) => n + c.length, 0);
            if (!total) return null;
            const all = new Float32Array(total);
            let off = 0;
            for (const c of pcm.chunks) { all.set(c, off); off += c.length; }
            const { samples, rate } = await resampleTo16k(all, pcm.rate);
            return encodeWav(samples, rate);
        } catch { return null; }
    }

    async start() {
        try {
            const opened = performance.now();
            this.stream = await navigator.mediaDevices.getUserMedia({ audio: MIC_CONSTRAINTS });
            this._openMs = Math.round(performance.now() - opened);
            this.audioChunks = [];
            const preferredTypes = RECORDER_TYPES;
            const mimeType = preferredTypes.find(t => MediaRecorder.isTypeSupported(t)) || '';
            this.mediaRecorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : {});
            // Read back the actual mimeType the browser chose — critical on iOS where
            // WebKit ignores the preferred list and defaults to video/mp4.
            this.mimeType = this.mediaRecorder.mimeType || mimeType || 'audio/webm';

            this.mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    this.audioChunks.push(event.data);
                }
            };

            this.mediaRecorder.start(1000);
            this._startSpeaking();
            this._diagWatch('start');
            this._startPcmTap();
            this._armMaxTimer();
            return true;
        } catch (err) {
            console.error('Failed to start recording:', err);
            this.lastErrorName = err.name || '';
            this.lastErrorMessage = err.message || '';
            return err.name || 'error';
        }
    }

    // Pre-warm: acquire the microphone stream without starting the MediaRecorder.
    // Call this early (e.g. while TTS is playing) so the stream is ready instantly
    // when startFromStream() is called at the moment the beep fires.
    async preWarm(constraints = MIC_CONSTRAINTS) {
        try {
            const opened = performance.now();
            this.stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
            this._openMs = Math.round(performance.now() - opened);
            return true;
        } catch (err) {
            console.error('Failed to pre-warm microphone:', err);
            this.lastErrorName = err.name || '';
            this.lastErrorMessage = err.message || '';
            // Return the DOMException name (e.g. NotAllowedError) so the caller can
            // tag the analytics error_type; callers must treat anything !== true as failure.
            return err.name || 'error';
        }
    }

    // Start recording from an already-open stream (synchronous — no getUserMedia latency).
    // Must call preWarm() first and await it.
    startFromStream() {
        if (!this.stream) return false;
        try {
            this.audioChunks = [];
            const preferredTypes = RECORDER_TYPES;
            const mimeType = preferredTypes.find(t => MediaRecorder.isTypeSupported(t)) || '';
            this.mediaRecorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : {});
            this.mimeType = this.mediaRecorder.mimeType || mimeType || 'audio/webm';
            this.mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) this.audioChunks.push(event.data);
            };
            this.mediaRecorder.start(1000);
            this._startSpeaking();
            this._diagWatch('from_stream');
            this._startPcmTap();
            this._armMaxTimer();
            return true;
        } catch (err) {
            console.error('Failed to start recorder from stream:', err);
            return false;
        }
    }

    // Practice minutes run only while the recorder is actually recording.
    _startSpeaking() {
        this._endSpeaking();
        this._endActive = markActive();
    }

    _endSpeaking() {
        if (this._endActive) { this._endActive(); this._endActive = null; }
    }

    stop() {
        this._endSpeaking();
        this._clearMaxTimer();
        this.diag?.mark('stop_called', { state: this.mediaRecorder?.state });
        this._diagUnwatch();
        return new Promise((resolve) => {
            if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
                // Recorder may already be inactive because the max-duration timer
                // stopped it — still deliver the captured audio to the caller.
                if (this.audioChunks.length > 0) {
                    this.audioBlob = new Blob(this.audioChunks, { type: this.mimeType || 'audio/webm' });
                    this.audioUrl = URL.createObjectURL(this.audioBlob);
                    if (this.stream && !this.keepStream) this.stream.getTracks().forEach(t => t.stop());
                    this._finishPcmTap().then((wav) => {
                        this.audioBlob.fallbackWav = wav;
                        resolve({ url: this.audioUrl, blob: this.audioBlob, mimeType: this.mimeType || 'audio/webm', diag: this.diag });
                    });
                    return;
                }
                resolve(null);
                return;
            }

            this.mediaRecorder.onstop = () => {
                this.audioBlob = new Blob(this.audioChunks, { type: this.mimeType || 'audio/webm' });
                this.audioUrl = URL.createObjectURL(this.audioBlob);
                if (this.stream && !this.keepStream) {
                    this.stream.getTracks().forEach(track => track.stop());
                }
                this._finishPcmTap().then((wav) => {
                    this.audioBlob.fallbackWav = wav;
                    resolve({ url: this.audioUrl, blob: this.audioBlob, mimeType: this.mimeType || 'audio/webm', diag: this.diag });
                });
            };

            this.mediaRecorder.stop();
        });
    }

    getAudioUrl() {
        return this.audioUrl;
    }

    cleanup() {
        this._endSpeaking();
        this._clearMaxTimer();
        this._finishPcmTap();
        if (this.audioUrl) {
            URL.revokeObjectURL(this.audioUrl);
            this.audioUrl = null;
        }
        const stopTracks = () => {
            if (this.stream && !this.keepStream) {
                this.stream.getTracks().forEach(track => track.stop());
            }
        };
        if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
            this.mediaRecorder.onstop = stopTracks;
            this.mediaRecorder.stop();
        } else {
            stopTracks();
        }
    }
}

// Voice activity detection: resolves when the user stops speaking.
// Waits for SILENCE_MS of continuous silence after at least MIN_SPEECH_MS of speech.
// Falls back silently if AudioContext is unavailable.
export const waitForSilence = (stream, { minSpeechMs = 500, silenceMs = 700, threshold = 0.01, diag = null,
                                         adaptive = false } = {}) => {
    return new Promise((resolve) => {
        let ctx, analyser, source, interval;
        try {
            ctx = new (window.AudioContext || window.webkitAudioContext)();
            analyser = ctx.createAnalyser();
            analyser.fftSize = 512;
            source = ctx.createMediaStreamSource(stream);
            source.connect(analyser);
        } catch {
            resolve('fallback');
            return;
        }

        const data = new Float32Array(analyser.fftSize);
        let speechStarted = false;
        let silenceStart = null;
        const startTime = Date.now();
        // The loudest level heard so far. A quiet laptop mic carried Leanne's
        // speech at 0.002 to 0.009, under the fixed 0.01, so the check decided
        // she had stopped while she was talking and ended the recording (3 of
        // 4 desktop recordings, 19 Oct 2026). Speech is now anything above a
        // quarter of the loudest level, never above the old fixed line, so a
        // normal mic behaves exactly as before.
        let peak = 0;

        const cleanup = () => {
            clearInterval(interval);
            try { source.disconnect(); ctx.close(); } catch {}
        };

        let tick = 0;
        interval = setInterval(() => {
            analyser.getFloatTimeDomainData(data);
            const rms = Math.sqrt(data.reduce((sum, v) => sum + v * v, 0) / data.length);
            const now = Date.now();
            const elapsed = now - startTime;
            // Every 100 ms, the level the check saw (diagnostics only).
            if (diag && (tick++ % 2 === 0)) diag.mark('level', { rms: Math.round(rms * 10000) / 10000 });

            // Total silence (a mic dropout) is not the speaker stopping: a real
            // pause still carries room noise. It neither ends nor resets anything.
            // Adaptive for everyone since 19 Oct 2026 (Automatic passes it).
            if (adaptive && rms < 0.00002) return;
            peak = Math.max(peak, rms);
            const speaking = adaptive
                ? rms > Math.max(0.0015, Math.min(threshold, peak * 0.25))
                : rms > threshold;
            if (speaking) {
                if (elapsed >= minSpeechMs && !speechStarted) diag?.mark('speech_started');
                if (elapsed >= minSpeechMs) speechStarted = true;
                silenceStart = null;
            } else if (speechStarted) {
                if (!silenceStart) silenceStart = now;
                else if (now - silenceStart >= silenceMs) {
                    diag?.mark('silence_stop');
                    cleanup();
                    resolve('silence');
                }
            }
        }, 50);
    });
};

// Measure the loudness of a recorded audio blob: RMS (average) and peak, each
// 0..1. Used to diagnose "too quiet" recordings on iOS (autoGainControl is off,
// so faint speech stays faint). Returns nulls if the clip can't be decoded.
export const measureLoudness = async (blob) => {
    if (!blob) return { rms: null, peak: null };
    let ctx;
    try {
        const arrayBuf = await blob.arrayBuffer();
        ctx = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuf = await ctx.decodeAudioData(arrayBuf);
        const data = audioBuf.getChannelData(0);
        let sumSq = 0, peak = 0;
        for (let i = 0; i < data.length; i++) {
            const v = data[i];
            sumSq += v * v;
            const a = v < 0 ? -v : v;
            if (a > peak) peak = a;
        }
        const rms = Math.sqrt(sumSq / data.length);
        return { rms: Math.round(rms * 1000) / 1000, peak: Math.round(peak * 1000) / 1000 };
    } catch {
        return { rms: null, peak: null };
    } finally {
        if (ctx) ctx.close().catch(() => {});
    }
};

// Adaptive volume boost for quiet recordings, applied to the UPLOAD path only
// (never to mic constraints — toggling autoGainControl broke iPhone playback
// routing). iOS captures ~50x quieter than Android/desktop; scaling the gain to
// each recording's measured RMS gives the server a healthy signal while leaving
// normal-volume recordings untouched.
const BOOST_RMS_THRESHOLD = 0.05; // only boost below this — 2x above the measured iOS
                                  // ceiling (~0.024) but below quiet-ish desktop recordings
                                  // (~0.08+), which the server-side loudnorm handles fine
// No low floor: real iOS speech routinely lands at rms ~0.0015-0.004 (audible on
// playback, spiky crest factor), which a 0.002 floor wrongly excluded as "silence"
// — the raw clip then tripped the server's too-quiet gate. Boosting genuine
// silence is safe: uniform gain preserves crest factor, and true silence still
// lands below the server's too-quiet threshold after boost (or Gemini returns an
// empty transcript → score 0). See boostQuietRecording below.
const BOOST_TARGET_RMS = 0.2;    // aim for a comfortably loud but unclipped level
const BOOST_MAX_GAIN = 24;       // ~27 dB cap so near-silence isn't blown up into loud noise

// Resample a mono Float32 buffer to 16 kHz via OfflineAudioContext (the browser
// applies proper anti-aliasing). 16 kHz is ample for speech analysis and cuts the
// WAV to ~1/3 the size (~470KB→~155KB for 5s), keeping the upload well under
// automatic mode's 15s analysis timeout. Falls back to the source rate if
// resampling isn't available, so the boost still works — just with a larger file.
const TARGET_SAMPLE_RATE = 16000;
const resampleTo16k = async (samples, srcRate) => {
    if (!samples.length || srcRate <= TARGET_SAMPLE_RATE) return { samples, rate: srcRate };
    try {
        const OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!OfflineCtx) return { samples, rate: srcRate };
        const outLen = Math.max(1, Math.ceil((samples.length / srcRate) * TARGET_SAMPLE_RATE));
        const offline = new OfflineCtx(1, outLen, TARGET_SAMPLE_RATE);
        const srcBuf = offline.createBuffer(1, samples.length, srcRate);
        srcBuf.copyToChannel(samples, 0);
        const node = offline.createBufferSource();
        node.buffer = srcBuf;
        node.connect(offline.destination);
        node.start();
        const rendered = await offline.startRendering();
        return { samples: rendered.getChannelData(0), rate: TARGET_SAMPLE_RATE };
    } catch {
        return { samples, rate: srcRate };
    }
};

// Encode an AudioBuffer's first channel as a 16-bit PCM mono WAV (browsers can't
// re-encode opus/mp4, but WAV is universal and the backend transcodes anyway).
const encodeWav = (samples, sampleRate) => {
    const buf = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buf);
    const writeStr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
    writeStr(0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);   // fmt chunk size
    view.setUint16(20, 1, true);    // PCM
    view.setUint16(22, 1, true);    // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true); // byte rate
    view.setUint16(32, 2, true);    // block align
    view.setUint16(34, 16, true);   // bits per sample
    writeStr(36, 'data');
    view.setUint32(40, samples.length * 2, true);
    for (let i = 0, off = 44; i < samples.length; i++, off += 2) {
        const s = Math.max(-1, Math.min(1, samples[i]));
        view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return new Blob([buf], { type: 'audio/wav' });
};

// Returns { blob, mimeType, boosted, gain, rms }. On any failure — or when the
// recording is already loud enough — the original blob/mimeType pass through
// unchanged, so a boost problem can never lose a recording.
export const boostQuietRecording = async (blob, mimeType) => {
    const passthrough = { blob, mimeType, boosted: false, gain: 1, rms: null };
    if (!blob) return passthrough;
    let ctx;
    try {
        const arrayBuf = await blob.arrayBuffer();
        ctx = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuf = await ctx.decodeAudioData(arrayBuf);
        const data = audioBuf.getChannelData(0);
        let sumSq = 0, peak = 0;
        for (let i = 0; i < data.length; i++) {
            const v = data[i];
            sumSq += v * v;
            const a = v < 0 ? -v : v;
            if (a > peak) peak = a;
        }
        const rms = Math.sqrt(sumSq / data.length);
        passthrough.rms = rms;
        if (!rms || rms >= BOOST_RMS_THRESHOLD) return passthrough;
        // Per-recording gain: reach the target RMS without clipping (0.95/peak)
        // and without amplifying near-silence into loud noise (MAX_GAIN). A
        // uniform gain preserves the crest factor, so the server's peak-to-mean
        // no-speech detection still works on boosted audio.
        const gain = Math.min(BOOST_TARGET_RMS / rms, peak > 0 ? 0.95 / peak : BOOST_MAX_GAIN, BOOST_MAX_GAIN);
        if (gain <= 1) return passthrough;
        const scaled = new Float32Array(data.length);
        for (let i = 0; i < data.length; i++) scaled[i] = data[i] * gain;
        // Downsample to 16 kHz mono to keep the WAV small and the upload fast.
        const { samples: outSamples, rate: outRate } = await resampleTo16k(scaled, audioBuf.sampleRate);
        return {
            blob: encodeWav(outSamples, outRate),
            mimeType: 'audio/wav',
            boosted: true,
            gain: Math.round(gain * 10) / 10,
            rms: Math.round(rms * 1000) / 1000,
        };
    } catch {
        return passthrough;
    } finally {
        if (ctx) ctx.close().catch(() => {});
    }
};

// Everything heard so far in one string, rebuilt from scratch on every event so
// it can never double up. Some browsers (Android Chrome) return each result as
// the whole sentence so far ("I", "I have", "I have a"...) instead of just the
// new words; a result that starts with the previous one replaces it.
export const mergeRecognitionResults = (results) => {
    const chunks = [];
    for (let i = 0; i < results.length; i++) {
        const t = (results[i][0]?.transcript || '').trim();
        if (!t) continue;
        const last = chunks[chunks.length - 1];
        if (last && t.toLowerCase().startsWith(last.toLowerCase())) chunks[chunks.length - 1] = t;
        else chunks.push(t);
    }
    return chunks.join(' ');
};

// Speech Recognition utility
export class SpeechRecognizer {
    // `options.continuous` keeps listening across pauses, which is what
    // dictating a sentence or two needs. It stays off by default because the
    // pronunciation practice callers want a single utterance and nothing else.
    // `options.onError` surfaces failures (denied mic, no speech) to the UI;
    // without it they only ever reached the console.
    constructor(onResult, onInterim, lang = 'fr-FR', options = {}) {
        this.recognition = null;
        this.onResult = onResult;
        this.onInterim = onInterim;
        this.lang = lang;
        this.isListening = false;
        this.continuous = options.continuous === true;
        this.onError = options.onError || null;
        this.onEnd = options.onEnd || null;
        this.onTranscript = options.onTranscript || null;

        this.init();
    }

    init() {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            console.error('Speech recognition not supported');
            return;
        }

        this.recognition = new SpeechRecognition();
        this.recognition.continuous = this.continuous;
        this.recognition.interimResults = true;
        this.recognition.lang = this.lang;
        this.recognition.maxAlternatives = 1;

        this.recognition.onresult = (event) => {
            if (this.onTranscript) {
                this.onTranscript(mergeRecognitionResults(event.results));
                return;
            }
            let finalTranscript = '';
            let interimTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalTranscript += transcript;
                } else {
                    interimTranscript += transcript;
                }
            }

            if (finalTranscript && this.onResult) {
                this.onResult(finalTranscript);
            }
            if (interimTranscript && this.onInterim) {
                this.onInterim(interimTranscript);
            }
        };

        this.recognition.onerror = (event) => {
            console.error('Speech recognition error:', event.error);
            this.isListening = false;
            if (this.onError) this.onError(event.error);
        };

        this.recognition.onend = () => {
            this.isListening = false;
            if (this.onEnd) this.onEnd();
        };
    }

    start() {
        if (this.recognition && !this.isListening) {
            try {
                this.recognition.start();
                this.isListening = true;
            } catch (e) {
                console.error('Failed to start recognition:', e);
            }
        }
    }

    stop() {
        if (this.recognition && this.isListening) {
            this.recognition.stop();
            this.isListening = false;
        }
    }

    isSupported() {
        return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    }
}

// Load voices (needed for some browsers)
export const loadVoices = () => {
    return new Promise((resolve) => {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
            resolve(voices);
            return;
        }

        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            resolve(window.speechSynthesis.getVoices());
        };

        window.speechSynthesis.onvoiceschanged = finish;
        // Safety net: some Android browsers never fire onvoiceschanged. Resolve
        // anyway after a short wait so callers aren't blocked indefinitely.
        setTimeout(finish, 1500);
    });
};

// Parse CSV content
export const parseCSV = (content) => {
    const lines = content.trim().split('\n');
    const phrases = [];
    
    for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        // Handle CSV with quotes
        const parts = line.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g);
        if (parts && parts.length >= 1) {
            const french = parts[0]?.replace(/^"|"$/g, '').trim();
            const english = parts[1]?.replace(/^"|"$/g, '').trim() || '';
            const phonetic = parts[2]?.replace(/^"|"$/g, '').trim() || '';
            
            if (french) {
                phrases.push({ french, english, phonetic });
            }
        }
    }
    
    return phrases;
};
